import { useId } from 'react';
import { routeOffsets, type RecordedLink, type Side } from '../../engine';

/**
 * A small engraving of a recorded route (inscriptions and Epitaphs): the start as a ring, each
 * landing as a dot, the last as an arrowhead pointing the way the route goes; the line deepens
 * from the start to the end. Offsets are in board coordinates, drawn from the viewer's orientation.
 */
export function RouteGlyph({
  links,
  size = 36,
  flip = false,
  title,
  className = '',
}: {
  links: readonly RecordedLink[];
  size?: number;
  /** True when the board is shown from North's side. */
  flip?: boolean;
  title?: string;
  className?: string;
}) {
  const gradient = `route-${useId().replace(/:/g, '')}`;
  const pts = [{ dx: 0, dy: 0 }, ...routeOffsets(links)];
  const xs = pts.map((p) => (flip ? -p.dx : p.dx));
  const ys = pts.map((p) => (flip ? p.dy : -p.dy));
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const span = Math.max(maxX - minX, maxY - minY, 1);
  const pad = 1.2;
  const scale = (size - pad * 2 * (size / 12)) / span;
  const ox = (size - (maxX - minX) * scale) / 2;
  const oy = (size - (maxY - minY) * scale) / 2;
  const points = xs.map(
    (x, i) => [ox + (x - minX) * scale, oy + ((ys[i] as number) - minY) * scale] as const,
  );
  const width = Math.max(1.4, size / 17);
  const [sx, sy] = points[0]!;
  const [ex, ey] = points[points.length - 1]!;
  // The arrowhead sits on the last landing, along the last segment; the line stops at its base.
  const [px, py] = points[points.length - 2] ?? points[0]!;
  const length = Math.hypot(ex - px, ey - py) || 1;
  const [ux, uy] = [(ex - px) / length, (ey - py) / length];
  const head = Math.min(size / 5, length * 0.8);
  const base = [ex - ux * head * 0.5, ey - uy * head * 0.5] as const;
  const tip = [ex + ux * head * 0.5, ey + uy * head * 0.5] as const;
  const wing = head * 0.5;
  const arrow = [
    tip,
    [base[0] - uy * wing, base[1] + ux * wing],
    [base[0] + uy * wing, base[1] - ux * wing],
  ]
    .map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`)
    .join(' ');
  const d = [...points.slice(0, -1), base]
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`)
    .join(' ');
  return (
    <svg
      className={`route-glyph ${className}`}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      <defs>
        <linearGradient
          id={gradient}
          gradientUnits="userSpaceOnUse"
          x1={sx}
          y1={sy}
          x2={ex}
          y2={ey}
        >
          <stop offset="0" stopColor="currentColor" stopOpacity="0.55" />
          <stop offset="1" stopColor="currentColor" />
        </linearGradient>
      </defs>
      <path
        d={d}
        fill="none"
        stroke={`url(#${gradient})`}
        strokeWidth={width}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={sx}
        cy={sy}
        r={size / 11}
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.75"
        strokeWidth={Math.max(1, size / 26)}
      />
      {points.slice(1, -1).map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={size / 13} fill="currentColor" />
      ))}
      <polygon points={arrow} fill="currentColor" strokeLinejoin="round" />
    </svg>
  );
}

export const flipFor = (viewer: Side): boolean => viewer === 'N';
